/* eslint-disable no-case-declarations */
import { Pipe, PipeTransform, LOCALE_ID, Inject } from '@angular/core';

import moment from 'moment';
import get from 'lodash-es/get';
import { Dictionary } from '../models';
import { FieldDef } from '@core/models';
import { AccountService } from 'app/core';
import { RelatedToType } from '@shared/enums';
import { TsCurrencyPipe } from './ts-currency.pipe';
import { FormatNumberPipe } from './format-number.pipe';
import { Project, Property } from '@modules/projects/projects.types';

type ModelType = Project | Property | Dictionary<any>;

@Pipe({
  name: 'readableField',
  pure: true,
})
export class ReadableFieldPipe implements PipeTransform {
  constructor(
    @Inject(LOCALE_ID) public locale: string,
    private readonly accountService: AccountService,
  ) {}

  transform(value: any, model: ModelType, fieldDef: FieldDef, optionsDictionary: { [key: string]: any }): string {
    if (fieldDef === undefined || fieldDef === null) {
      return value;
    }

    if (fieldDef.type !== 'select' && fieldDef.name.includes('.')) {
      return value;
    }

    if (fieldDef?.name === 'property_ids') {
      return value;
    }

    switch (fieldDef?.type) {
      case 'number':
        return new FormatNumberPipe().transform(value, fieldDef.meta?.scale || 0, fieldDef);
      case 'percent':
        if (value !== null && value !== undefined && value !== '') {
          return `${new FormatNumberPipe().transform(value, fieldDef.meta?.scale || 0, fieldDef)}%`;
        }

        return '';
      case 'decimal':
        return new FormatNumberPipe().transform(value, fieldDef.meta?.scale || 0, fieldDef);
      case 'money':
        // eslint-disable-next-line no-case-declarations
        const precision = fieldDef.meta?.scale || 0;
        return new TsCurrencyPipe(this.accountService).transform(
          value,
          'USD',
          'symbol',
          '1.' + precision + '-' + precision,
        );
      case 'date':
        return value ? moment(value).format('L') : null;
      case 'datetime':
        return value ? moment(value).utc().format('L') : null;
      case 'select':
        if (fieldDef?.meta?.multi_select) {
          let fieldValue = fieldDef?.name?.split('.')?.length > 1 ? get(model, fieldDef.name) : model[fieldDef.name];
          // Use the passed value if it's not undefined, otherwise use the field value from the model
          let finalValue = value !== undefined ? value : fieldValue;

          finalValue = this.convertIfItIsValidJson(finalValue);
          return Array.isArray(finalValue) ? finalValue.join(', ') : finalValue;
        }

        const taskUsersLabel = this.mapTaskUserIdsToNames(value, fieldDef, optionsDictionary);
        if (taskUsersLabel !== null) {
          return taskUsersLabel;
        }

        const contactsLabel = this.mapContactIdsToNames(value, fieldDef, optionsDictionary);
        if (contactsLabel !== null) {
          return contactsLabel;
        }

        const taskSectionsLabel = this.mapTaskSectionIdsToNames(value, fieldDef, optionsDictionary);
        if (taskSectionsLabel !== null) {
          return taskSectionsLabel;
        }

        // eslint-disable-next-line no-case-declarations
        let options = [];
        if (!fieldDef.meta.option_name) {
          options = fieldDef.meta?.options ?? [];
          options = fieldDef.meta.options.map((value) => ({ id: value, name: value }));
        } else {
          options = optionsDictionary[fieldDef.meta.option_name]?.filter((option) => !option?.hidden) ?? [];
        }

        //[sc-12306]: re-enabled and uncommented code
        options = options.filter((option) => {
          if (option.hidden) {
            return false;
          } else {
            if (option?.pipeline_ids) {
              if (model.pipeline_id) {
                return option?.pipeline_ids.includes(Number(model.pipeline_id));
              }
            }
          }
          return true;
        });

        // eslint-disable-next-line no-case-declarations
        const selectedOption =
          options.find(({ id }) => id == value) ||
          options.find(({ name, full_name }) => name === value || full_name === value);

        return selectedOption?.name || selectedOption?.full_name;
      case 'checkbox':
        const stringValue = String(value);
        // Handle boolean string values
        if (stringValue === 'true') {
          return 'Yes';
        } else if (stringValue === 'false') {
          return 'No';
        }
        // Handle already readable values (Yes/No)
        else if (stringValue === 'Yes') {
          return 'Yes';
        } else if (stringValue === 'No') {
          return 'No';
        }
        // Handle null, undefined, or empty values
        else if (value === null || value === undefined || value === '') {
          return 'Not Set';
        }
        // Fallback for any other values
        else {
          return 'Not Set';
        }
      case 'record':
        value = this.convertIfItIsValidJson(value);
        if (value === null) {
          return '';
        }

        if (
          fieldDef.name.startsWith('cf_linked_') ||
          (typeof value === 'string' && value?.includes('|')) ||
          value instanceof Array
        ) {
          return value instanceof Array
            ? value
                .map((val) => {
                  if (typeof val === 'string' && val.includes('|')) {
                    return val?.split('|')[1];
                  }

                  return val;
                })
                .join(', ')
            : value?.split('|')[1];
        }

        return value;
      case 'id':
        return value?.label;
      case 'url':
      case 'typeahead':
      case 'tags':
      case 'year':
      case 'textarea':
      case 'text':
      default:
        return value;
    }
  }

  /**
   * Map contact IDs to names if the field is a contact_id field and the options dictionary contains a contactsById property.
   */
  private mapContactIdsToNames(
    value: unknown,
    fieldDef: FieldDef,
    optionsDictionary: { [key: string]: any },
  ): string | null {
    if (
      fieldDef?.related_to_type !== RelatedToType.Task ||
      fieldDef?.name !== 'contact_id' ||
      !optionsDictionary?.contactsById
    )
      return null;

    const mapById = optionsDictionary.contactsById as Record<string, string>;

    if (value === null || value === undefined || value === '') {
      return '';
    }

    if (Array.isArray(value)) {
      return value
        .filter((v) => v !== null && v !== undefined && v !== '')
        .map((v) => {
          const key = String(v);
          return mapById[key] ?? key;
        })
        .join(', ');
    }

    const key = String(value);
    return mapById[key] ?? key;
  }

  private mapTaskUserIdsToNames(
    value: unknown,
    fieldDef: FieldDef,
    optionsDictionary: { [key: string]: any },
  ): string | null {
    if (fieldDef?.related_to_type !== RelatedToType.Task) return null;

    const isApproversOrFollowersField = fieldDef?.name === 'approver_id' || fieldDef?.name === 'following_user_id';
    if (!isApproversOrFollowersField) return null;

    const users = optionsDictionary?.users as Array<{ id: number | string; name?: string }> | undefined;
    if (!Array.isArray(users) || users.length === 0) return null;

    if (value === null || value === undefined || value === '') {
      return '';
    }

    const nameById = new Map<string, string>();
    for (const u of users) {
      const id = u?.id;
      const name = (u?.name || '').trim();
      if (id === null || id === undefined || !name) continue;
      nameById.set(String(id), name);
    }

    const parsed = this.convertIfItIsValidJson(value);
    if (Array.isArray(parsed)) {
      return parsed
        .filter((v) => v !== null && v !== undefined && v !== '')
        .map((v) => {
          const key = String(v);
          return nameById.get(key) ?? key;
        })
        .join(', ');
    }

    const key = String(parsed);
    return nameById.get(key) ?? key;
  }

  /**
   * Map task section IDs to names if the field is a task_section_id field and the options dictionary contains a taskSectionsById property.
   */
  private mapTaskSectionIdsToNames(
    value: unknown,
    fieldDef: FieldDef,
    optionsDictionary: { [key: string]: any },
  ): string | null {
    if (
      fieldDef?.related_to_type !== RelatedToType.Task ||
      fieldDef?.name !== 'task_section_id' ||
      !optionsDictionary?.taskSectionsById
    )
      return null;

    const mapById = optionsDictionary.taskSectionsById as Record<string, string>;

    if (value === null || value === undefined || value === '') {
      return '';
    }

    if (Array.isArray(value)) {
      return value
        .filter((v) => v !== null && v !== undefined && v !== '')
        .map((v) => {
          const key = String(v);
          return mapById[key] ?? key;
        })
        .join(', ');
    }

    const key = String(value);
    return mapById[key] ?? key;
  }

  private convertIfItIsValidJson(value: unknown): any {
    if (typeof value !== 'string') return value;
    try {
      return JSON.parse(value);
    } catch {
      return value;
    }
  }
}
